import { Redirect, useParams } from "react-router-dom";
import { SmartApplyTemplate } from "../../../../template/smart-apply/default-template";
import { SmartApplyR } from "../../route";
import { SmartApplyMenuList } from "../../../../organisms/smart-apply/menu-list";
import ReactDatePicker from "react-datepicker";
import {
  InformationBanner,
  PrimaryButton,
  RadioButton,
  SecondaryButton,
  Spinner,
  TextField,
} from "@graffer-inc/console-design-kit";
import { useQualifiedInvoiceNewContainer } from "./hooks";
import { QualifiedInvoiceNewPreviewContainer } from "./preview";
import { TabUiState } from "../../../../../application/service/smart-apply/application-detail/tab-ui-state";
import { convertToFormattedId } from "@graffer-inc/cloud-gov-core";

export const QualifiedInvoiceNewContainer: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  const {
    isLoading,
    redirectTo,
    procedureId,
    procedureGroupId,
    issueRequest,
    detailPayment,
    amountDetail,
    format,
    transactionDate,
    transactionDateError,
    buyerName,
    buyerNameError,
    taxAmountDetail,
    taxAmountDetailError,
    disabledIssueButton,
    handleOnRedirectToApplicationDetail,
    handleOnChangeFormat,
    handleOnChangeTransactionDate,
    handleOnChangeBuyerName,
    handleOnChangeTaxAmountDetail,
    handleOnIssue,
  } = useQualifiedInvoiceNewContainer({
    id,
  });

  if (redirectTo === "application-detail") {
    return (
      <Redirect
        to={SmartApplyR.ApplicationDetail.link({
          id,
          tab: TabUiState.payment(),
        })}
      />
    );
  }

  return (
    <SmartApplyTemplate
      breadcrumbs={[
        { to: SmartApplyR.Applications.link({}), title: "申請一覧" },
        {
          to: SmartApplyR.ApplicationDetail.link({
            id,
            tab: TabUiState.payment(),
          }),
          title: convertToFormattedId(id),
        },
        {
          to: SmartApplyR.ApplicationQualifiedInvoiceNew.link({ id }),
          title: "支払い明細書の交付",
        },
      ]}
      menuList={<SmartApplyMenuList />}
    >
      <Spinner loading={isLoading} />
      <div
        style={{
          maxWidth: "800px",
        }}
      >
        {!!issueRequest && (
          <>
            <div className="level">
              <div className="level-left">
                <div className="level-item">
                  <h1 className="title">支払い明細書の交付</h1>
                </div>
              </div>
              <div className="level-right">
                <div className="level-item">
                  <PrimaryButton
                    text="交付する"
                    type="button"
                    disabled={disabledIssueButton}
                    onClick={() =>
                      handleOnIssue({
                        applicationId: id,
                        qualifiedInvoiceIssueRequestId: issueRequest.id,
                      })
                    }
                  />
                </div>
                <div className="level-item">
                  <SecondaryButton
                    text="キャンセル"
                    type="button"
                    onClick={handleOnRedirectToApplicationDetail}
                  />
                </div>
              </div>
            </div>
            <div className="mb-4">
              <InformationBanner
                message={
                  "インボイスの形式に応じて必要な項目を入力してください。\n入力内容に誤りがある場合、再交付が必要となるため、ご確認の上交付してください。"
                }
              />
            </div>
            <div className="field">
              <RadioButton
                label={"インボイスの形式"}
                description={""}
                options={[
                  {
                    value: "SIMPLIFIED",
                    name: "適格簡易請求書",
                  },
                  {
                    value: "STANDARD",
                    name: "適格請求書",
                  },
                ]}
                required={true}
                value={format}
                onChange={(e) => handleOnChangeFormat(e.target.value)}
                direction={"row"}
              />
            </div>
            <div className="field">
              <label className="label">取引年月日</label>
              <p className="is-size-6 mb-2">
                役務の提供が完了した日を入力してください。
              </p>
              <div className="control">
                <ReactDatePicker
                  className="input"
                  placeholderText="----/--/--"
                  selected={transactionDate}
                  dateFormat="yyyy/MM/dd"
                  onChange={(date) =>
                    handleOnChangeTransactionDate(date as Date)
                  }
                />
              </div>
              {transactionDateError && (
                <p className="mt-2 is-size-7 has-text-danger">
                  {transactionDateError}
                </p>
              )}
            </div>
            {format === "STANDARD" && (
              <>
                <div className="field">
                  <TextField
                    label="交付を受ける申請者の氏名又は名称"
                    description={""}
                    placeholder={""}
                    value={buyerName}
                    required={true}
                    errorMessages={buyerNameError}
                    onChange={(e) => handleOnChangeBuyerName(e.target.value)}
                  />
                </div>
                <label className="label">消費税額</label>
                <p className="is-size-6 mb-2">
                  端数処理により、消費税額に差分が生じる場合は、正しい税額を入力してください。
                </p>
                {/* 消費税額は0円の場合でも補正を行う可能性があるので0円以上を補正可能な条件とする */}
                {amountDetail["0.1"]! > 0 && taxAmountDetail["0.1"]! >= 0 && (
                  <div className="field is-flex is-align-items-flex-end">
                    <div>
                      <TextField
                        label="消費税率 10%"
                        type="text"
                        description={"半角数字で入力してください"}
                        placeholder={""}
                        value={taxAmountDetail["0.1"]}
                        required={true}
                        fullWidth={false}
                        errorMessages={taxAmountDetailError["0.1"]}
                        onChange={(e) =>
                          handleOnChangeTaxAmountDetail({
                            taxRate: "0.1",
                            value: parseInt(e.target.value, 10),
                          })
                        }
                      />
                    </div>
                  </div>
                )}
                {amountDetail["0.08"]! > 0 && taxAmountDetail["0.08"]! >= 0 && (
                  <div className="field is-flex is-align-items-flex-end">
                    <div>
                      <TextField
                        label="消費税率 8%"
                        type="text"
                        description={"半角数字で入力してください"}
                        placeholder={""}
                        value={taxAmountDetail["0.08"]}
                        required={true}
                        fullWidth={false}
                        errorMessages={taxAmountDetailError["0.08"]}
                        onChange={(e) =>
                          handleOnChangeTaxAmountDetail({
                            taxRate: "0.08",
                            value: parseInt(e.target.value, 10),
                          })
                        }
                      />
                    </div>
                  </div>
                )}
              </>
            )}
            {procedureId ? (
              <div className="mt-6">
                <QualifiedInvoiceNewPreviewContainer
                  applicationId={id}
                  procedureId={procedureId}
                  groupId={procedureGroupId}
                  buyerName={format === "STANDARD" ? buyerName : ""}
                  transactionDate={{
                    year: transactionDate.getFullYear(),
                    month: transactionDate.getMonth() + 1,
                    day: transactionDate.getDate(),
                  }}
                  detailPayment={{
                    ...detailPayment!,
                    taxAmountDetail:
                      format === "STANDARD" ? taxAmountDetail : {},
                  }}
                />
              </div>
            ) : null}
          </>
        )}
      </div>
    </SmartApplyTemplate>
  );
};
